import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { Partner } from "../../models/Partner";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import {
  setShow,
  setCurrentUser,
  setModalDelete,
  setDataToRemove,
  setSortBy,
  setDefaultSort,
  setPage,
} from "../../store/slices/colaboradoresSlice.ts";
import { Tooltip } from '@mui/material';
import { fixedHeadStyle, fixedStyle } from "../../utils/constanst.js";
import moment from "moment";
import "moment/locale/es";
moment.locale("es");

const TableColaboradoresSuspendidos = ({
  hideReactivate = false,
  showApprover = false,
}: {
  hideReactivate?: boolean;
  showApprover?: boolean;
}) => {
  const dispatch = useDispatch();
  const { colaboradores, fetch, sortBy, defaultSort } = useSelector(
    (state: RootState) => state.colaboradores
  );

  const formatDate = (date: string) => {
    if (!date) return "N/A";
    return moment(date).format("DD/MM/YYYY");
  };

  const getIconSort = () => {
    return (
      <i
        className={`fa-solid fa-sort-${defaultSort === "asc" ? "up" : "down"}`}
      />
    );
  };

  const setSort = (sort: string) => {
    const sortOrder =
      sortBy !== sort ? "asc" : defaultSort === "asc" ? "desc" : "asc";

    dispatch(setSortBy(sort));
    dispatch(setPage(0));
    dispatch(setDefaultSort(sortOrder));
  };

  return (
    <div className="table-responsive mb-3" style={{ overflowY: "auto", minHeight: '600px' }}>
      <table className="table table-hover table-panel text-nowrap align-middle mb-0">
        <thead>
          <tr style={{ ...fixedHeadStyle } as React.CSSProperties}>
            <th 
              style={{ ...fixedStyle, cursor: "pointer" } as React.CSSProperties}
              onClick={() => setSort("id")}
            >
              Id {sortBy === "id" && getIconSort()}
            </th>
            <th 
              style={{ cursor: "pointer" }}
              onClick={() => setSort("name")}
            >
              Nombre completo {sortBy === "name" && getIconSort()}
            </th>
            <th 
              style={{ cursor: "pointer" }}
              onClick={() => setSort("phone")}
            >
              Celular {sortBy === "phone" && getIconSort()}
            </th>
            <th 
              style={{ cursor: "pointer" }}
              onClick={() => setSort("position")}
            >
              Puesto {sortBy === "position" && getIconSort()}
            </th>
            <th 
              style={{ cursor: "pointer" }}
              onClick={() => setSort("shift")}
            >
              Turno {sortBy === "shift" && getIconSort()}
            </th>
            <th 
              style={{ cursor: "pointer" }}
              onClick={() => setSort("branch")}
            >
              Sucursal {sortBy === "branch" && getIconSort()}
            </th>
            <th style={{ cursor: "default" }}>
              Fecha de suspensión
            </th>
            <th style={{ cursor: "default" }}>
              Motivo de suspensión
            </th>
            {showApprover && (
              <th style={{ cursor: "default" }}>
                Aprobó
              </th>
            )}
            <th style={{ cursor: "default" }}>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {colaboradores?.length > 0 ? (
            colaboradores.map((colaborador: Partner, index) => (
              <tr key={index}>
                <td style={{ ...fixedStyle} as React.CSSProperties}>{colaborador.id}</td>
                <td>
                  {toCamelCase(
                    `${colaborador.person.firstName} ${colaborador.person.lastName} ${colaborador.person.secondLastName}`
                  )}
                </td>
                <td>{colaborador.person.phone}</td>
                <td>{colaborador.candidate?.vacancy?.position.name}</td>
                <td>{colaborador.candidate?.shift?.name}</td>
                <td>{colaborador.candidate?.branch?.name}</td>
                <td>
                  {colaborador.updateAt 
                    ? formatDate(colaborador.updateAt)
                    : "N/A"}
                </td>
                <td>
                  <Tooltip 
                    title={colaborador.reasonUnsubscribe || "Sin motivo especificado"} 
                    arrow
                  >
                    <div style={{
                      maxWidth: "300px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      cursor: "help"
                    }}>
                      {colaborador.reasonUnsubscribe || "Sin motivo especificado"}
                    </div>
                  </Tooltip>
                </td>
                {showApprover && (
                  <td>
                    {(() => {
                      const requests = (colaborador as any).unsubscribeRequests || [];
                      if (!requests.length) return <span className="text-muted">-</span>;
                      const sorted = [...requests].sort(
                        (a: any, b: any) => (b.id || 0) - (a.id || 0)
                      );
                      const latest = sorted[0];
                      const approver = latest?.authorizingUser;
                      return approver?.name
                        ? approver.name
                        : approver?.fullName
                        ? approver.fullName
                        : approver
                        ? `${approver.firstName || ""} ${approver.lastName || ""}`.trim() || "-"
                        : <span className="text-muted">-</span>;
                    })()}
                  </td>
                )}
                <td>
                  {!hideReactivate && (
                    <Tooltip title="Reactivar colaborador" arrow>
                      <button
                        className="btn btn-success btn-sm m-2"
                        onClick={() => {
                          dispatch(setShow(true));
                          dispatch(
                            setCurrentUser({
                              id: colaborador.id,
                              name: `${colaborador.person.firstName} ${colaborador.person.lastName} ${colaborador.person.secondLastName}`,
                              curp: colaborador.person.curp,
                            })
                          );
                        }}
                      >
                        <i className="fa-solid fa-check"></i>
                      </button>
                    </Tooltip>
                  )}
                  {(() => {
                    const reason = (colaborador.reasonUnsubscribe || "").toLowerCase();
                    const showBajaAction =
                      colaborador.partnerStatus.name === "Suspendido" &&
                      (reason.includes("baja por aprobar") ||
                        !!colaborador.programedUnsubscribed);

                    if (!showBajaAction) return null;

                    return (
                      <Tooltip title="Aprobar o rechazar baja" arrow>
                        <button
                          className="btn btn-warning btn-sm m-2"
                          onClick={() => {
                            dispatch(setModalDelete(true));
                            dispatch(
                              setDataToRemove({
                                id: colaborador.id,
                                name: `${colaborador.person.firstName} ${colaborador.person.lastName} ${colaborador.person.secondLastName}`,
                                programed: colaborador.programedUnsubscribed,
                              })
                            );
                          }}
                        >
                          <i className="fa-solid fa-scale-balanced"></i>
                        </button>
                      </Tooltip>
                    );
                  })()}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={9}>
                <div className="py-4">
                  <div className="d-flex">
                    <div className="mx-auto text-center">
                      <i className="fa-solid fa-user-slash fa-3x text-secondary mb-3"></i>
                      <h5 className="text-secondary fs-20px">
                        No hay colaboradores suspendidos
                      </h5>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default TableColaboradoresSuspendidos;

